import {
  CheckCircleOutlined,
  DeleteOutlined,
  RollbackOutlined,
  ShareAltOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { Inventory, InventoryTransfer } from '@hefe/models';
import { currentUserSelector, InventoryApi } from '@hefe/store';
import { ActionType, inventoryTransferActionTypes } from '@hefe/utils';
import { Button, Popconfirm, Tooltip } from 'antd';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { environment } from '../../../../../environments/environment';
import CreateInventoryAdminReturnRequestForm from '../../forms/create-inventory-admin-return-request/create-inventory-admin-return-request.form';

interface InventoryTransferActionsComponentProps {
  inventory: Inventory;
  transfer: InventoryTransfer;
}

export default function InventoryTransferActionsComponent(
  props: InventoryTransferActionsComponentProps
) {
  const { inventory, transfer } = props;

  const activeUser = useSelector(currentUserSelector);

  const [deleteTransfer, { isLoading: isDeletingTransfer }] =
    InventoryApi.useDeleteTransferMutation();

  const [confirmReturn, { isLoading: isConfirmingReturn }] =
    InventoryApi.useConfirmReturnMutation();

  const [createShareLink, { isLoading: isCreatingShareLink }] =
    InventoryApi.useCreateShareLinkMutation();

  const [requestAdminReturnModalOpen, setRequestAdminReturnModalOpen] =
    useState(false);

  const Actions = useMemo(() => {
    const returnValue = [];

    const actionTypes = inventoryTransferActionTypes(
      inventory,
      transfer,
      activeUser
    );

    if (actionTypes.includes(ActionType.CONFIRM_RETURN)) {
      returnValue.push(
        <Popconfirm
          key="delete"
          title={t('inventory.transfers.return.confirm.description')}
          onConfirm={() => confirmReturn(transfer)}
          disabled={isConfirmingReturn}
        >
          <Button
            key="confirm_return"
            type="text"
            icon={<CheckCircleOutlined />}
            loading={isConfirmingReturn}
          />
        </Popconfirm>
      );
    }

    if (actionTypes.includes(ActionType.CREATE_SHARE_LINK)) {
      returnValue.push(
        <Popconfirm
          key="create_share_link"
          title={t('inventory.transfers.share.create.description')}
          onConfirm={() => createShareLink(inventory)}
          disabled={isCreatingShareLink}
        >
          <Tooltip title={t('inventory.transfers.share.create.title')}>
            <Button
              key="create_share_link"
              type="text"
              icon={<UserSwitchOutlined />}
              loading={isCreatingShareLink}
            />
          </Tooltip>
        </Popconfirm>
      );
    }

    if (actionTypes.includes(ActionType.SHOW_SHARE_LINK)) {
      returnValue.push(
        <Popconfirm
          key="show_share_link"
          title={`${environment.frontendUrl}/#/user/inventories/${transfer.share_transfer_id}`}
        >
          <Tooltip title={t('inventory.transfers.share.show_link')}>
            <Button
              key="show_share_link"
              type="text"
              icon={<ShareAltOutlined />}
            />
          </Tooltip>
        </Popconfirm>
      );
    }

    if (actionTypes.includes(ActionType.REQUEST_RETURN)) {
      returnValue.push(
        <Tooltip title={t('inventory.transfers.return.admin_request.title')}>
          <Button
            key="request_return"
            type="text"
            icon={<RollbackOutlined />}
            onClick={() => setRequestAdminReturnModalOpen(true)}
          />
        </Tooltip>
      );
    }

    if (actionTypes.includes(ActionType.DELETE)) {
      returnValue.push(
        <Popconfirm
          key="delete"
          title={t('inventory.transfers.delete.description')}
          onConfirm={() => deleteTransfer(transfer)}
          disabled={isDeletingTransfer}
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            loading={isDeletingTransfer}
          />
        </Popconfirm>
      );
    }

    return returnValue;
  }, [
    activeUser,
    confirmReturn,
    createShareLink,
    deleteTransfer,
    inventory,
    isConfirmingReturn,
    isDeletingTransfer,
    transfer,
  ]);

  return (
    <div>
      {Actions}
      <CreateInventoryAdminReturnRequestForm
        inventory={inventory}
        open={requestAdminReturnModalOpen}
        setOpen={(open) => setRequestAdminReturnModalOpen(open)}
      />
    </div>
  );
}
